import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiChecklistEditGeneralComponent } from './general/general.component';
import { NgModel } from '@angular/forms';
import { SideListComponent } from '@fi-app/utilities/side-list/side-list.component';
import { AppTextService } from '@fi-shared/app-text';
import { ChecklistService } from '../checklist.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiChecklistEditSharingSelectionComponent } from '@fi-app/checklist/edit/sharing-selection/sharing-selection.component';
import { first } from 'rxjs/operators';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { Subject } from 'rxjs';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { FiChecklistEditSettingsComponent } from '@fi-app/checklist/edit/settings/settings.component';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { SideListOptionType } from '@fi-shared/types';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';

@Component({
    selector: 'fi-checklist-edit',
    templateUrl: './edit.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./edit.component.scss'],
    viewProviders: [
        {
            provide: 'defaultLanguageId',
            useFactory: (fiUserDataService: FiUserDataService) => fiUserDataService.getContentLanguage(),
            deps: [FiUserDataService],
        },
    ],
    standalone: false,
})
export class FiChecklistEditComponent implements OnInit, AfterViewInit, AfterViewChecked {
    @Input() checklist: ChecklistModel;
    @ViewChild(FiChecklistEditGeneralComponent, { static: true }) checklistEditGeneral: FiChecklistEditGeneralComponent;
    @ViewChild('settings', { static: false }) checklistEditSettings: FiChecklistEditSettingsComponent;
    @ViewChild('checklistEditForm', { static: true }) checklistEditForm: FiValidatingFormDirective;
    @ViewChild('sideList', { static: true }) sideList: SideListComponent;
    @ViewChild(FiChecklistEditSharingSelectionComponent, { static: false })
    checklistEditSharingSelection: FiChecklistEditSharingSelectionComponent;

    MobileLanguages = {
        Show: false,
    };

    selectedLanguage: number;

    templateChanged$ = new Subject<void>();
    contentChanged$ = new Subject<void>();

    constructor(
        @Inject('defaultLanguageId') public defaultLanguageId: number,
        private appTextService: AppTextService,
        private checklistService: ChecklistService,
        private loadingService: LoadingService,
        private saveChangesWarningService: SaveChangesWarningService,
        private stateService: FiStateService,
        private cdRef: ChangeDetectorRef,
        private customerDataService: FiCustomerDataService,
        private fiDialogsService: FiDialogsService,
        private fiContentLanguageSharedService: FiContentLanguageSharedService,
    ) {}

    ngOnInit() {
        this.selectedLanguage = !!this.checklist.Id
            ? this.fiContentLanguageSharedService.getContentLanguageIdByContentTranslation(this.checklist.Names)
            : this.defaultLanguageId;
    }

    ngAfterViewInit(): void {
        this.checklistEditSharingSelection.accessRightFilters.onInitialized$.pipe(first()).subscribe(() => {
            this.saveChangesWarningService.start(() => this.checklist);
        });
    }

    ngAfterViewChecked() {
        this.cdRef.detectChanges();
    }

    // note: not used ?
    onLanguageChange($event) {}

    /*    isNotSharedAndDefaultNameFilledOut() { todo  should we have here this check?
        /!* return !this.checklist.PublicationSetting.IsShared &&
            this.checklistEditForm.controls['Name_' + this.defaultLanguageId].valid;*!/
    }*/

    save() {
        //if (this.checklistEditForm.valid || this.isNotSharedAndDefaultNameFilledOut()) {
        this.loadingService.trackPromise(this.checklistService.saveCheckList(this.checklist)).then((data) => {
            this.checklist.Id = data;
            this.saveChangesWarningService.stop();
            this.checklistEditSharingSelection.accessRightFilters.OnAccessRightFilterCollectionSaved();
            if (this.checklist.IsTemplate) {
                this.stateService.go(FiStateNames.Checklist.Template.List);
                return;
            }
            this.stateService.go(FiStateNames.Checklist.List, { filterType: SideListOptionType.CurrentCheckLists });

            /*if (this.checklistEditForm.invalid && this.isNotSharedAndDefaultNameFilledOut()) {
                            this.dialogs.showNotification(this.appTextService.get('checklist.saved_not_shared_required'));
                        }*/
        });
        //} else {
        //    this.dialogs.showNotification(this.appTextService.get('common.error_all_mandatory'));
        //}
    }

    onValidateError(inputsWithErrors: NgModel[]) {
        const valueAccessors = inputsWithErrors.map((ngModel) => ngModel.valueAccessor);
        let errorMessage = this.appTextService.get('common.error_all_mandatory');
        if (valueAccessors.indexOf(this.checklistEditSettings) !== -1) {
            this.checklistEditSettings.expandContent();
        }
        if (valueAccessors.indexOf(this.checklistEditSharingSelection.accessRightFilters) !== -1) {
            errorMessage = this.checklistEditSharingSelection.accessRightFilters.getErrorMessage();
            this.checklistEditSharingSelection.expandContent();
        }

        this.fiDialogsService.notify({
            messageText: errorMessage,
        });

        this.updateLanguageValidations();
    }

    shouldShowQRCodeDataList() {
        return this.customerDataService.customerData.ChecklistPresenceEnabled && this.checklist.CheckPresenceEnabled;
    }

    checklistValidationChange() {
        this.updateLanguageValidations();
    }

    updateLanguageValidations() {
        const nameModelList = this.checklistEditGeneral.nameModelList;
        this.checklist.Names.forEach((item, index) => {
            this.setLanguageErrorState(
                item.ContentLanguageId,
                this.checklistEditForm.submitted && nameModelList[index].invalid,
            );
        });
    }

    setLanguageErrorState(langId, showError) {
        this.sideList.ccmSideList.Data.some((item) => {
            if (item.Id == langId) {
                item.$$showError = showError;
                return true;
            }
        });
    }

    onTemplateChanged() {
        this.templateChanged$.next();
        this.contentChanged$.next();
    }
}
